import { CollectionConfig } from "@rebasepro/types";

/**
 * Prospect Activity — what was actually done with an employer, in order.
 *
 * The prospect's `notes` field held everything and therefore answered nothing
 * about sequence: an employer emailed twice in a week by two people and one not
 * touched for a month read identically in a text box. Each act is a row here —
 * status changes, emails, calls, meetings, and the discovery runs that found
 * the contacts — so the account has a history rather than a paragraph.
 *
 * Append-only by convention: nothing in the app updates or deletes a row. A log
 * that can be rewritten is the text box again, wearing a table's clothes. What
 * `notes` is for now is standing context about the account, which is what it
 * was always trying to be.
 *
 * `actor` is a string, not a relation: the log outlives the account that wrote
 * it, and an entry that loses its author when somebody leaves is worse than one
 * holding a name nobody can join on.
 */
const prospectActivityCollection: CollectionConfig = {
    name: "Actividad de empresas",
    singularName: "Actividad",
    slug: "prospect-activity",
    table: "prospect_activity",
    securityRules: [
        {
            name: "admin_full_access",
            operation: "all",
            roles: ["admin"]
        }
    ],
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid"
        },
        employer_key: {
            name: "Clave de empresa",
            type: "string",
            validation: { required: true },
            admin: { readOnly: true }
        },
        contact_id: {
            name: "Persona",
            type: "string",
            description: "La persona con quien fue, si fue con una persona"
        },
        kind: {
            name: "Tipo",
            type: "string",
            enum: [
                { id: "note", label: "Nota", color: "gray" },
                { id: "status", label: "Cambio de estado", color: "blue" },
                { id: "email", label: "Email", color: "cyan" },
                { id: "call", label: "Llamada", color: "orange" },
                { id: "meeting", label: "Reunión", color: "purple" },
                { id: "discovery", label: "Búsqueda de personas", color: "green" }
            ],
            defaultValue: "note"
        },
        body: {
            name: "Detalle",
            type: "string",
            admin: { multiline: true }
        },
        actor: {
            name: "Quién",
            type: "string",
            admin: { readOnly: true }
        },
        created_at: {
            name: "Cuándo",
            type: "date",
            autoValue: "on_create"
        }
    },
    admin: {
        group: "Captación",
        icon: "History",
        sort: ["created_at", "desc"],
        // Fuera del menú: esta tabla es el interior de "Empresas que contratan",
        // no una pantalla. Como entrada del menú era tres cosas confusas a la
        // vez — vacía hasta que alguien actúa, sin explicar qué es, y un
        // segundo camino peor hacia esa pantalla.
        //
        // `group` por sí solo NO la ocultaba: el menú muestra cualquier
        // colección que declare uno, esté o no en `navigationGroupMappings`.
        // Sigue siendo navegable — el lápiz de la pantalla abre la fila.
        hideFromNavigation: true
    }
};

export default prospectActivityCollection;
